import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getHrBlogById,
  addHrBlog,
  editHrBlog,
  deleteHrBlog,
} from "../utils/methods";
import { QuillEditor, UploadImage, InputComp } from "../components";
import { strings } from "../utils/strings";
import UserIcon from "../components/Icons/userIcon";
import Button from "../components/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import useFetchOptions from "../hooks/useFetchOptions";
import { toast } from "react-toastify";

const HrBlogForm = () => {
  const { id: currentId } = useParams();
  const navigate = useNavigate();
  const [hrBlogData, setHrBlogData] = useState({});
  const [author, setAuthor] = useState("");
  const [Category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [blogVersion, setBlogVersion] = useState("");
  const [hrBlogImageUrl, setHrBlogImageUrl] = useState("");
  const [hrBlogImageFile, setHrBlogImageFile] = useState(null);

  const { authors, wikkiCategories, loading, error } = useFetchOptions();

  useEffect(() => {
    const loadHrBlogData = async () => {
      if (currentId !== "new") {
        try {
          const hrBlog = await getHrBlogById(currentId);
          setHrBlogData(hrBlog);
          setAuthor(hrBlog.author || "");
          setCategory(hrBlog.category || "");
          setTitle(hrBlog.title || "New Blog");
          setBlogVersion(hrBlog.version || "");
          setHrBlogImageUrl(hrBlog.blogImage || "");
        } catch (error) {
          console.error("Error loading blog:", error);
        }
      }
    };
    loadHrBlogData();
  }, [currentId]);

  const handleTitleChange = (key, newTitle) => {
    setTitle(newTitle);
    setHrBlogData((prevData) => ({ ...prevData, [key]: newTitle }));
  };

  const handleInputChange = (key, value) => {
    setHrBlogData((prev) => ({ ...prev, [key]: value }));
  };

  const handleOptionChange = (key, newValue) => {
    setHrBlogData((prevData) => ({
      ...prevData,
      [key]: newValue || "",
    }));

    switch (key) {
      case "Category":
        setCategory(newValue || "");
        break;
      case "author":
        setAuthor(newValue || "");
        break;
      default:
        break;
    }
  };

  const handleImageUpload = (url, file) => {
    setHrBlogImageUrl(url);
    setHrBlogImageFile(file);
    setHrBlogData((prevData) => ({ ...prevData, blogImage: url }));
  };

  const formatTitleToId = (title) => {
    return title.trim().replace(/\s+/g, "-").toLowerCase();
  };

  const handleSave = async () => {
    const fieldsToValidate = [
      {
        key: "title",
        value: hrBlogData.title,
        label: strings["inputTitle"],
        regex: null,
      },
      {
        key: "version",
        value: hrBlogData.version,
        label: strings["blogVersion"],
        regex: /^\d+(\.\d+)*$/,
      },
      {
        key: "Category",
        value: Category,
        label: strings["inputCategory"],
        regex: null,
      },
      {
        key: "author",
        value: author,
        label: strings["authorName"],
        regex: null,
      },
      {
        key: "blogImage",
        value: hrBlogData.blogImage,
        label: strings["blogImage"],
        regex: null,
      },
      {
        key: "content",
        value: hrBlogData.content,
        label: "Content",
        regex: null,
      },
    ];

    let isFormValid = true;

    fieldsToValidate.forEach(({ key, value, label, regex }) => {
      if (!value || (regex && !regex.test(value))) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid.`);
      }
    });
    if (!isFormValid) return;

    try {
      const newHrBlogId = formatTitleToId(title);
      if (currentId !== "new" && currentId !== newHrBlogId) {
        await addHrBlog({ ...hrBlogData, title }, newHrBlogId);
        await deleteHrBlog(currentId);
      } else if (currentId === "new") {
        await addHrBlog({ ...hrBlogData, title }, newHrBlogId);
      } else {
        await editHrBlog(currentId, hrBlogData, hrBlogImageFile);
      }
      navigate(`/wikkipedia`);
    } catch (error) {
      console.error("Error saving blog:", error);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading options: {error.message}</p>;

  return (
    <div className="bg-border w-full h-full overflow-scroll xl:px-[212px] lg:px-[100px] md:px-[60px] pt-12 pb-[60px]">
      <p className="text-[36px] font-semibold">
        {currentId === "new"
          ? `${strings["addBlogTitle"]} ${hrBlogData.title || "New"}`
          : `${strings["EditBlogTitle"]} ${hrBlogData.title}`}
      </p>

      {/* Title */}

      <InputComp
        label={strings["inputTitle"]}
        placeHolder={strings["titlePlaceholder"]}
        type="text"
        value={title}
        handleChange={handleTitleChange}
        inputKey="title"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      {/* Description */}
      <InputComp
        label={strings["blogVersion"]}
        placeHolder={strings["versionPlaceholder"]}
        type="text"
        value={hrBlogData.version || ""}
        handleChange={handleInputChange}
        inputKey="version"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      {/* Type Selector */}
      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium text-color-1">
            {strings["inputCategory"]}
          </label>
        </div>
        <Autocomplete
          className="w-full"
          options={wikkiCategories}
          getOptionLabel={(option) => option || ""}
          value={Category}
          onChange={(event, newValue) =>
            handleOptionChange("Category", newValue)
          }
          disableClearable // Removes the close button
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
        />
      </div>

      {/* Authors Name Selector */}
      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
            {strings["authorName"]}
          </label>
        </div>
        <Autocomplete
          className="w-full"
          options={authors}
          getOptionLabel={(option) => option || ""}
          value={author}
          onChange={(event, newValue) => handleOptionChange("author", newValue)}
          disableClearable // Removes the close button
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} className="flex items-center gap-x-2">
              <UserIcon />
              <div className="pt-1">{option}</div>
            </li>
          )}
        />
      </div>

      <div className="mt-[20px]">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1 mt-[20px]">
            {strings["blogImage"]}
          </label>
        </div>

        {/* Blog Image */}
        <UploadImage
          onImageUpload={handleImageUpload}
          blogId={currentId === "new" ? null : currentId}
          imageType="blogImages"
          initialImageUrl={hrBlogData.blogImage} // Pass the initial image URL
        />
      </div>

      {/* HTML Editor */}
      <div className="my-6 bg-white ">
        <QuillEditor
          value={hrBlogData.content || ""}
          onChange={(template) => handleInputChange("content", template)}
        />
      </div>

      {/* Upload Button */}
      <div className="flex justify-center w-full">
        <Button classes="!w-full justify-center" onClick={handleSave}>
          {strings["submitButton"]}
        </Button>
      </div>
    </div>
  );
};

export default HrBlogForm;
